import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gsApi } from "../auth/auth";
import {
  organizations,
  getThunkResponse,
  deleteThunkResponse,
  createThunkResponse,
  thunkResponse,
  organizationUsers,
} from "../app/api";
import {
  getOpts,
  getResponse,
  getSerializedData,
  shortDelay,
  mediumDelay,
  extraLongDelay,
} from "../helpers/JSONapi";

// fetch
export const getOrganizationUsers = createAsyncThunk(
  "users/getOrganizationUsers",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${organizations}/${id}/organization-users`
    );
    return response;
  }
);

export const getOrganizationUser = createAsyncThunk(
  "users/getOrganizationUser",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${organizationUsers}/${id}`);
    return response;
  }
);

// delete
export const deleteOrganizationUser = createAsyncThunk(
  "users/deleteOrganizationUser",
  async (id, thunkAPI) => {
    const response = await gsApi.delete(`${organizationUsers}/${id}`, {
      data: {},
    });
    return response;
  }
);

// add
export const addOrganizationUser = createAsyncThunk(
  "users/addOrganizationUser",
  async (orgUser, thunkAPI) => {
    const serializedData = getSerializedData(
      orgUser,
      "organization-user",
      getOpts(orgUser, getOrganizationRelationships(orgUser))
    );
    return await getResponse(
      gsApi.post,
      `${organizationUsers}`,
      serializedData,
      thunkAPI
    );
  }
);

// resend invitation
export const resendUserInvitation = createAsyncThunk(
  "users/resendUserInvitation",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${organizationUsers}/${id}/resend-invitation`,
      {},
      thunkAPI
    );
  }
);

export const userSlice = createSlice({
  name: "users",
  initialState: {
    selectedOrganizationMembers: [],
    selectedMember: {},
    notifications: [],
    loading: [],
  },
  reducers: {
    setMember: (state, action) => {
      state.selectedMember = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: {
    ...getThunkResponse(getOrganizationUsers, (state, action) => {
      const members = action.payload.map?.((member) => ({
        ...member,
        type: member.role === "tournament_organizer" ? "Tournament Organizer" : `Organization ${member.role.charAt(0).toUpperCase() + member.role.slice(1)}`,
      }));
      state.selectedOrganizationMembers = members;
    }),
    ...getThunkResponse(getOrganizationUser, (state, action) => {state.selectedMember = action.payload;}),
    ...deleteThunkResponse(deleteOrganizationUser, (state, action) => {
      const filteredList = state.selectedOrganizationMembers?.filter?.(
        (member) => member.id !== action.meta.arg
      );
      state.selectedOrganizationMembers = filteredList;
    }, "Organization Member", shortDelay),
    ...createThunkResponse(addOrganizationUser, (state, action) => {
      const member = { ...action.payload, type: "Organization Member" };
      state.selectedOrganizationMembers = [
        member,
        ...state.selectedOrganizationMembers,
      ];
      state.selectedMember = member;
    }, "Member", shortDelay),
    // resend invitations
    ...thunkResponse(resendUserInvitation, (state, action) => {}, "Member Invitation", {
      pendingDelay: mediumDelay,
      fulfilledDelay: mediumDelay,
      rejectedDelay: extraLongDelay,
      pendingText: "Resending Invitation...",
      fulfilledText: "An invitation has been sent.",
    }),
  },
});

export const { clearNotifications, setMember } = userSlice.actions;

export default userSlice.reducer;

export const selectUsersNotifications = (state) =>
  state.users.notifications;

export const selectOrganizationMembers = (state) =>
  state.users.selectedOrganizationMembers;

export const selectMember = (state) => state.users.selectedMember;

export const selectLoading = (state) => state.users.loading;

function getOrganizationRelationships() {
  const relationships = [];
  relationships.push({
    name: "organization",
    opt: { ref: "id", included: true },
  });
  return relationships;
}
